import { FC, FocusEvent } from "react";

import { useFormikContext } from "formik";

import { withCompositeFormikField } from "@lib/withFormikField";

import AsyncOptionSelect from "./AsyncOptionSelect";
import CustomAsyncOptionSelect from "./CustomAsyncOptionSelect";
import FixedOptionSelect from "./FixedOptionSelect";
import type { TSelectProps } from "./types";

const Select: FC<TSelectProps> = ({
  id,
  name,
  options,
  value,
  defaultOption,
  hint,
  noSuggestionsMessage,
  onChange,
  onFocus,
  onBlur,
  setFieldValue,
  loadOptions,
  isLoading = false,
  isReadOnlySelection = false,
  isNotClearable = false,
  shouldClearOnSelect = false,
  searchWithOnlyBeginnings = false,
  mode = "fixedOption",
  ...rest
}) => {
  const { setFieldTouched } = useFormikContext();

  const handleBlur = (event: FocusEvent<HTMLInputElement>) => {
    onBlur?.(event);

    if (name) {
      setFieldTouched(name, true);
    }
  };

  if (!options && !loadOptions) {
    throw new Error("`options` or `loadOptions` prop must be provided for Select component");
  }

  if (mode === "fixedOption") {
    return (
      <FixedOptionSelect
        options={options}
        noSuggestionsMessage={noSuggestionsMessage}
        isReadOnlySelection={isReadOnlySelection}
        isNotClearable={isNotClearable}
        setFieldValue={setFieldValue}
        onBlur={handleBlur}
        onChange={onChange}
        onFocus={onFocus}
        value={value}
        id={id}
        name={name}
        searchWithOnlyBeginnings={searchWithOnlyBeginnings}
        shouldClearOnSelect={shouldClearOnSelect}
        {...rest}
      />
    );
  }

  if (mode === "asyncOption") {
    return (
      <AsyncOptionSelect
        loadOptions={loadOptions}
        noSuggestionsMessage={noSuggestionsMessage}
        isReadOnlySelection={isReadOnlySelection}
        isNotClearable={isNotClearable}
        setFieldValue={setFieldValue}
        onBlur={handleBlur}
        onChange={onChange}
        onFocus={onFocus}
        value={value}
        defaultOption={defaultOption}
        id={id}
        name={name}
        shouldClearOnSelect={shouldClearOnSelect}
        {...rest}
      />
    );
  }
  if (mode === "customAsyncOption") {
    return (
      <CustomAsyncOptionSelect
        loadOptions={loadOptions}
        noSuggestionsMessage={noSuggestionsMessage}
        isNotClearable={isNotClearable}
        setFieldValue={setFieldValue}
        onBlur={handleBlur}
        onChange={onChange}
        onFocus={onFocus}
        value={value}
        defaultOption={defaultOption}
        id={id}
        isLoading={isLoading}
        name={name}
        {...rest}
      />
    );
  }

  throw new Error("Invalid Select mode");
};

export default withCompositeFormikField({ hasSetFieldValue: true })(Select);
